import React from 'react'
import { CircleX, Search } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import { normalizeTokenSearchQuery } from '@/features/tokens/importableTokenSearch'
import { Input } from '@/ui/input'
import { cn } from '@/lib/utils'

const labels = nutrimaticLabels.mobileSimple

function SearchClearButton({
  onClear,
  className,
  variant = 'nutrimatic',
}: {
  onClear: () => void
  className?: string
  variant?: 'nutrimatic' | 'sheet'
}) {
  return (
    <button
      type="button"
      className={cn(
        variant === 'nutrimatic'
          ? 'nm-mobile-simple__picker-search-clear'
          : 'absolute right-3 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground',
        className,
      )}
      onClick={onClear}
      aria-label={labels.pickerClearSearch}
    >
      <CircleX className={variant === 'nutrimatic' ? 'size-4' : 'size-4'} strokeWidth={2.25} aria-hidden />
    </button>
  )
}

/** Campo de búsqueda unificado: símbolo, nombre o dirección de contrato. */
export function TokenPickerSearchField({
  value,
  onChange,
  variant = 'nutrimatic',
  className,
  autoFocus,
}: {
  value: string
  onChange: (value: string) => void
  variant?: 'nutrimatic' | 'sheet' | 'browse'
  className?: string
  autoFocus?: boolean
}) {
  const hasValue = value.trim().length > 0

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(normalizeTokenSearchQuery(event.target.value))
  }

  const handleClear = () => {
    onChange('')
  }

  if (variant === 'browse') {
    return (
      <div className={cn('mobile-app__search', hasValue && 'mobile-app__search--has-clear', className)}>
        <Search className="mobile-app__search-icon" aria-hidden />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={labels.pickerSearchPlaceholder}
          aria-label={labels.pickerSearchPlaceholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="text"
          className="mobile-app__search-input"
          autoFocus={autoFocus}
        />
        {hasValue ? <SearchClearButton onClear={handleClear} variant="sheet" /> : null}
      </div>
    )
  }

  if (variant === 'sheet') {
    return (
      <div className={cn('px-4 pt-1', className)}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={value}
            onChange={handleChange}
            placeholder={labels.pickerSearchPlaceholder}
            aria-label={labels.pickerSearchPlaceholder}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="text"
            className={cn('border-border bg-muted/30 pl-9', hasValue && 'pr-10')}
            autoFocus={autoFocus}
          />
          {hasValue ? <SearchClearButton onClear={handleClear} variant="sheet" /> : null}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('nm-mobile-simple__picker-search-wrap', className)}>
      <Search className="nm-mobile-simple__picker-search-icon" aria-hidden />
      <Input
        type="text"
        name="asset-search"
        value={value}
        onChange={handleChange}
        placeholder={labels.pickerSearchPlaceholder}
        aria-label={labels.pickerSearchPlaceholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode="text"
        enterKeyHint="done"
        className={cn(
          'nm-mobile-simple__picker-search shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
          hasValue && 'nm-mobile-simple__picker-search--has-clear',
        )}
        autoFocus={autoFocus}
      />
      {hasValue ? <SearchClearButton onClear={handleClear} variant="nutrimatic" /> : null}
    </div>
  )
}
